import React, { useEffect } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";

// Actions
import {
  fetchExportStudies,
  toggleExportStudiesDialog,
  fetchGetAvailableDrives,
} from "../../actions";

// MUI Components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import CircularProgress from "@material-ui/core/CircularProgress";

import TableSortLabel from "@material-ui/core/TableSortLabel";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import Paper from "@material-ui/core/Paper";
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";

// Icons
import RefreshIcon from "@material-ui/icons/Refresh";

// Other Files
import { customTheme } from "../../constants/customTheme";
import { colors } from "../../constants/colors";
import classNames from "classnames";

const useStyles = makeStyles({
  root: (props) => ({
    ...customTheme(props.theme).dialog.root,
    width: "calc(100% - 40px)",
    maxWidth: "calc(100% - 40px)",
    height: "calc(100% - 50px)",
  }),
  title: customTheme().dialog.title,
  contentContainer: (props) => ({
    ...customTheme().dialog.contentContainer,
    ...customTheme(props.theme).customScrollBar,
    overflowY: "hidden",
    width: "100%",
    padding: "8px 0px",
  }),
  contentItem: {
    ...customTheme().dialog.contentItem,

    margin: "0px",
  },
  contentItemDropdown: {
    ...customTheme().dialog.contentItem,
    margin: "0px",
    width: "250px",
  },
  buttonContainer: customTheme().dialog.buttonContainer,
  buttonOutlined: (props) => ({
    width: "110px",
    ...customTheme(props.theme).buttonOutlined,
  }),
  buttonProgress: (props) => customTheme(props.theme).buttonProgress,
  buttonExport: (props) => ({
    width: "170px",
    minHeight: "36.5px",
    ...customTheme(props.theme).buttonPrimary,
  }),
  tableContainer: (props) => ({
    marginTop: "15px",
    height: "calc(100% - 185px)",
    maxHeight: "calc(100% - 185px)",
    "@media only screen and (max-width: 900px)": {
      height: "calc(100% - 185px)",
      maxHeight: "calc(100% - 185px)",
    },
    overflowY: "auto",
    ...customTheme(props.theme).customScrollBar,
  }),
  subHeaderRow: {
    backgroundColor: (props) => colors[props.theme].tableHeader,
    padding: "5px 0px 5px 0px",
  },
  subDataCell: {
    padding: "5px 0px 5px 0px",
    backgroundColor: (props) => colors[props.theme].tableSubRow,
  },
  select: {
    marginRight: "0px",
  },
  noData: {
    textAlign: "center",
    marginTop: "50px",
  },
  checkBox: {
    minWidth: "50px",
    padding: "0px",
  },
  tablePaperRoot: {
    height: "100%",
    width: "100%",
    borderRadius: "0px",
    boxShadow: "unset",
  },
  centerText: {
    display: "flex",
    justifyContent: "center",
    margin: "50px",
  },
  icon: customTheme().icon,
  activeSortIcon: {
    opacity: 1,
  },
  inActiveSortIcon: {
    opacity: 0.2,
  },
  progressCircle: {
    marginRight: "15px",
    color: "rgba(255,255,255,1)",
  },
  progressLabel: {
    fontSize: "0.9rem",
  },
  refreshIcon: {
    paddingBottom: "0px",
  },
  usbOptions: {
    display: "flex",
    alignItems: "flex-end",
  },
  exportLogCheckbox: {
    marginTop: "5px",
  },
});

const ExportStudiesDialog = (props) => {
  const classes = useStyles(props);
  const columnStudies = [
    {
      title: "Participant ID",
      field: "participantId",
      minWidth: "140px",
      paddingLeft: "20px",
    },
    {
      title: "Protocol Name",
      field: "protocolName",
      minWidth: "180px",
    },
    { title: "Date", field: "studyDate", minWidth: "110px" },
    {
      title: "User", //change the "user" to administrator?, credentials?
      field: "username",
      minWidth: "120px",
    },
    {
      title: "Progress",
      field: "progress",
      minWidth: "120px",
    },
  ];

  useEffect(() => {
    if (props.localStudiesReceived === true) {
      setLocalState({
        ...localState,
        dataTable: formatData(props.localStudyInfoById),
        selected: props.localStudyAllIds,
      });
    }
  }, [props.localStudiesReceived, props.localStudyInfoById]);

  // useEffect(() => {
  //   props.eventSource.addEventListener("progress_indicator", handleEvent);
  //   return () => {
  //     props.eventSource.removeEventListener("progress_indicator", handleEvent);
  //   };
  // }, []);

  const getWindowSize = () => {
    let newDataTableSize = window.innerHeight;
    if (window.innerHeight > 600) {
      newDataTableSize = window.innerHeight - 375; // These numbers are based on dialog size
    } else {
      newDataTableSize = window.innerHeight - 265; // These numbers are based on dialog size
    }
    return newDataTableSize;
  };

  useEffect(() => {
    // Measure window size and dynamicaly change the size of the data table
    const handleResize = () => {
      setLocalState({ ...localState, windowSize: getWindowSize() });
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount and unmount

  const formatData = (data) => {
    let dataTable = [];
    Object.keys(data).forEach((study) =>
      dataTable.push({
        id: study,
        participantId: data[study].participantId,
        protocolName: data[study].protocolName,
        username: data[study].username,
        progress:
          data[study].progress === null ? "NA" : data[study].progress + "%",
        studyDate: data[study].studyDate,
      })
    );

    return dataTable;
  };

  const [localState, setLocalState] = React.useState({
    selectedUsb: "",
    selected: [],
    dataTable: formatData(props.localStudyInfoById),
    windowSize: getWindowSize(),
    exportLog: false,
    order: "asc",
    orderBy: null,
  });
  // Circular progress value
  const [progressIndicator, setProgressIndicator] = React.useState(0);

  const handleSelectAllClick = (event) => {
    let newSelecteds = [];
    if (event.target.checked) {
      newSelecteds = [...localState.dataTable.map((study) => study.id)];
    }
    setLocalState({ ...localState, selected: newSelecteds });
  };

  const handleStudyCheckboxClick = (event, studyId) => {
    let newSelecteds;
    if (event.target.checked) {
      newSelecteds = [...localState.selected, studyId];
    } else {
      newSelecteds = localState.selected.filter((id) => {
        return id !== studyId;
      });
    }
    setLocalState({ ...localState, selected: newSelecteds });
  };

  const handleClickExportLog = (e) => {
    let allSelected = [...localState.dataTable.map((study) => study.id)];
    setLocalState({
      ...localState,
      selected: allSelected,
      exportLog: !localState.exportLog,
    });
  };

  const requestSort = (property) => (event) => {
    const isAsc = localState.orderBy === property && localState.order === "asc";
    setLocalState({
      ...localState,
      order: isAsc ? "desc" : "asc",
      orderBy: property,
    }); //toggle between
  };

  function descendingComparator(a, b, orderBy) {
    if (orderBy === "progress") {
      // Need a special case for the progress because progress can be a number (percentage) or NA
      let numA = a[orderBy];
      let numB = b[orderBy];
      if (a[orderBy] !== "NA") {
        numA = Number(a[orderBy].slice(0, -1));
      } else if (a[orderBy] === "NA") {
        numA = -1;
      }
      if (b[orderBy] !== "NA") {
        numB = Number(b[orderBy].slice(0, -1));
      } else if (b[orderBy] === "NA") {
        numB = -1;
      }
      if (numB < numA) {
        return -1;
      }
      if (numB > numA) {
        return 1;
      }
      return 0;
    } else if (orderBy === "studyDate") {
      let dateNumA = Number(
        a[orderBy].split("/")[2] +
          a[orderBy].split("/")[0] +
          a[orderBy].split("/")[1]
      );
      let dateNumB = Number(
        b[orderBy].split("/")[2] +
          b[orderBy].split("/")[0] +
          b[orderBy].split("/")[1]
      );
      if (dateNumB < dateNumA) {
        return -1;
      }
      if (dateNumB > dateNumA) {
        return 1;
      }
      return 0;
    } else {
      if (b[orderBy] < a[orderBy]) {
        return -1;
      }
      if (b[orderBy] > a[orderBy]) {
        return 1;
      }
      return 0;
    }
  }
  const handleEvent = (e) => {
    switch (e.type) {
      case "progress_indicator":
        setProgressIndicator(JSON.parse(e.data) * 100);
        break;
      default:
    }
  };
  function stableSort(array) {
    const stabilizedThis = array.map((el, index) => [el, index]);

    stabilizedThis.sort((a, b) => {
      if (localState.order === "desc") {
        return descendingComparator(a[0], b[0], localState.orderBy);
      } else {
        return -descendingComparator(a[0], b[0], localState.orderBy);
      }
    });
    return stabilizedThis.map((el) => el[0]);
  }

  return (
    <Dialog
      open={props.open}
      PaperProps={{ className: classNames(classes.root) }}
      disableBackdropClick={true}
    >
      <Typography
        className={classes.title}
        variant="h4"
        color="textPrimary"
        align="center"
      >
        Export Studies to USB
      </Typography>

      <DialogContent className={classes.contentContainer}>
        <Typography
          className={classes.contentItem}
          color="textPrimary"
          align="center"
        >
          Export studies by copying study data to a USB. Select the USB and the
          studies to be copied. The study data will remain on the device after
          exporting. Exporting studies may take a while.
        </Typography>
        <div className={classes.usbOptions}>
          <FormControl className={classes.contentItemDropdown} fullWidth>
            <InputLabel>Select USB *</InputLabel>
            <Select
              required
              className={classes.select}
              onChange={(event) =>
                setLocalState({
                  ...localState,
                  selectedUsb: event.target.value,
                })
              }
              value={localState.selectedUsb}
              MenuProps={{ className: classNames(classes.menuScroll) }}
            >
              {Object.keys(props.usbDrives).length === 0 ? (
                <MenuItem key={"noUSB"} disabled={true} value="">
                  No USB detected
                </MenuItem>
              ) : (
                Object.keys(props.usbDrives).map((usb) => {
                  return (
                    <MenuItem
                      key={props.usbDrives[usb]["name"]}
                      value={props.usbDrives[usb]["name"]}
                    >
                      {props.usbDrives[usb]["name"]}
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>
          <IconButton
            className={classes.refreshIcon}
            onClick={() => props.fetchGetAvailableDrives()}
          >
            <RefreshIcon />
          </IconButton>
        </div>

        <FormControlLabel
          className={classes.exportLogCheckbox}
          control={
            <Checkbox
              color="primary"
              onChange={(e) => handleClickExportLog(e)}
              checked={localState.exportLog}
              value={localState.exportLog}
            />
          }
          label="Export all studies and log files"
        />

        <Paper className={classes.tablePaperRoot}>
          {!props.localStudiesReceived ? (
            <div className={classes.centerText}>
              <CircularProgress className={classes.icon} size={25} />
              <Typography>Loading...</Typography>
            </div>
          ) : props.localStudyAllIds.length === 0 ? (
            <Typography className={classes.noData}>No study found</Typography>
          ) : (
            <TableContainer
              className={classes.tableContainer}
              style={{
                maxHeight: localState.windowSize,
                height: localState.windowSize,
              }}
            >
              <Table stickyHeader size="small">
                {/* SUBTABLE HEADER */}
                <TableHead>
                  <TableRow>
                    <TableCell
                      className={classes.subHeaderRow}
                      key={"subheaderCheckbox"}
                      align="left"
                    >
                      <Checkbox
                        className={classes.checkBox}
                        color="primary"
                        disabled={localState.exportLog}
                        indeterminate={
                          localState.selected.length > 0 &&
                          localState.selected.length <
                            Object.keys(localState.dataTable).length
                        }
                        checked={
                          Object.keys(localState.dataTable).length > 0 &&
                          localState.selected.length ===
                            Object.keys(localState.dataTable).length
                        }
                        onChange={(e) => handleSelectAllClick(e)}
                      />
                    </TableCell>
                    {columnStudies.map((subHeader) => (
                      <TableCell
                        className={classes.subHeaderRow}
                        key={"subheader" + subHeader.field}
                        align="left"
                        style={{
                          minWidth: subHeader.minWidth,

                          width: "auto",
                          paddingLeft: subHeader.paddingLeft
                            ? subHeader.paddingLeft
                            : "unset",
                        }}
                        sortDirection={
                          localState.orderBy === subHeader.field
                            ? localState.order
                            : false
                        }
                      >
                        <TableSortLabel
                          active={localState.orderBy === subHeader.field}
                          direction={
                            localState.orderBy === subHeader.field
                              ? localState.order
                              : "asc"
                          }
                          onClick={requestSort(subHeader.field)}
                          classes={{
                            // Override icon class based on activation state
                            icon:
                              localState.orderBy === subHeader.field
                                ? classes.activeSortIcon
                                : classes.inActiveSortIcon,
                          }}
                        >
                          {subHeader.title}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stableSort(localState.dataTable).map((row, index) => (
                    <TableRow
                      key={row.id}
                      style={{
                        backgroundColor: localState.selected.includes(row.id)
                          ? colors[props.theme].tableHover
                          : "unset",
                      }}
                    >
                      <TableCell
                        className={classes.subDataCell}
                        key={row.id + "Checkbox"}
                        align="left"
                      >
                        <Checkbox
                          className={classes.checkBox}
                          disabled={localState.exportLog}
                          color="primary"
                          checked={localState.selected.includes(row.id)}
                          onClick={(e) => handleStudyCheckboxClick(e, row.id)}
                        />
                      </TableCell>
                      {columnStudies.map((subHeader) => (
                        <TableCell
                          className={classes.subDataCell}
                          key={subHeader.field}
                          align="left"
                          style={{
                            paddingLeft:
                              subHeader.field === "participantId"
                                ? "20px"
                                : "unset",
                          }}
                        >
                          {row[subHeader.field]}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </DialogContent>

      <DialogActions className={classes.buttonContainer}>
        <Button
          disabled={props.requestExportStudies}
          onClick={() => props.onClose()}
          className={classes.buttonOutlined}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={() =>
            props.fetchExportStudies(
              localState.selectedUsb,
              localState.selected,
              localState.exportLog
            )
          }
          className={classes.buttonExport}
          variant="contained"
          color="primary"
          disabled={
            props.requestExportStudies ||
            localState.selected.length === 0 ||
            localState.selectedUsb === ""
          }
        >
          {props.requestExportStudies ? (
            <Box
              className={classes.progressCircle}
              position="relative"
              display="inline-flex"
            >
              <CircularProgress
                size={38}
                variant="static"
                value={progressIndicator}
              />
              <Box
                top={0}
                left={0}
                bottom={0}
                right={0}
                position="absolute"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Typography
                  className={classes.progressLabel}
                  variant="caption"
                  component="div"
                >{`${Math.round(progressIndicator)}%`}</Typography>
              </Box>
            </Box>
          ) : (
            "Export Studies"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      fetchExportStudies,
      toggleExportStudiesDialog,
      fetchGetAvailableDrives,
    },
    dispatch
  );
};

const mapStateToProps = (state) => {
  return {
    localStudyInfoById: state.availableStudies.localStudyInfoById,
    localStudyAllIds: state.availableStudies.localStudyAllIds,
    localStudiesReceived: state.availableStudies.localStudiesReceived,
    usbDrives: state.utility.usbDrives,
    requestExportStudies: state.utility.requestExportStudies,
    theme: state.utility.theme,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ExportStudiesDialog);
